import logger from "./logger";

/**
 * Activity stream helpers for the hybrid Recent Activity Feed.
 *
 * The frontend merges three independent streams:
 *   1. The existing fake generator (socialProofGenerator.js).
 *   2. A backbone of real, masked wins polled from /activity/public.
 *   3. The current user's own wins from /activity/me/recent (YOU stream).
 *
 * This file owns the pure helpers around streams 2 + 3:
 *   - localStorage TTL helpers for the "already shown" YOU tx-id set.
 *   - The per-tick row picker that chooses backbone vs fake while
 *     enforcing the existing no-adjacency rules.
 *
 * Component logic (timers, fetches, state) lives in SocialProofSection.
 * See docs/features/20-HYBRID_RECENT_ACTIVITY_FEED.md.
 */

// ─── Tunables ────────────────────────────────────────────────────────────

// localStorage key for the {txId: shownAtMs} map. Per-entry TTL is
// applied on read (SHOWN_SELF_TX_TTL_MS, owned by SocialProofSection
// and passed in — kept here as a default for direct callers).
const SHOWN_SELF_TX_STORAGE_KEY = "rooster.shownSelfTxIds.v1";
const DEFAULT_SHOWN_SELF_TX_TTL_MS = 10 * 60_000;

// Probability that a single ticker frame picks a backbone row instead
// of generating fake — only applied when the backbone pool has an
// unseen, non-adjacent candidate available.
const DEFAULT_BACKBONE_PICK_PROBABILITY = 0.22;

// Big-win thresholds, mirrored from SocialProofCard so the adjacency
// logic can classify a row without importing the component.
const BIG_WIN_THRESHOLD = { gold: 1000, cluck: 100 };

// ─── Internal helpers ────────────────────────────────────────────────────

const safeParse = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const isBigWinActivity = (activity) => {
  if (!activity || !activity.isWin) return false;
  const threshold = BIG_WIN_THRESHOLD[activity.currency];
  return (
    typeof threshold === "number" && Math.abs(activity.amount) >= threshold
  );
};

// Classify a row into the bucket the no-adjacency rules care about:
// 'loss', 'bigWin', or 'win'.
const classifyOutcome = (activity) => {
  if (!activity) return null;
  if (!activity.isWin) return "loss";
  return isBigWinActivity(activity) ? "bigWin" : "win";
};

// Does the candidate violate any of the four adjacency rules relative
// to the row currently at the head of the feed?
//
//   - same player twice in a row (username comparison, case-insensitive)
//   - same game twice in a row (game_code comparison)
//   - two BIG WINs in a row
//   - two LOSSES in a row
const violatesAdjacency = (candidate, lastShown) => {
  if (!candidate || !lastShown) return false;

  const lastUsername = (lastShown.user?.username || "").toLowerCase();
  const candidateUsername = (candidate.user?.username || "").toLowerCase();
  if (lastUsername && candidateUsername && lastUsername === candidateUsername) {
    return true;
  }

  const lastGame = lastShown.game?.game_code;
  const candidateGame = candidate.game?.game_code;
  if (lastGame && candidateGame && lastGame === candidateGame) {
    return true;
  }

  const lastOutcome = classifyOutcome(lastShown);
  const candidateOutcome = classifyOutcome(candidate);
  if (lastOutcome === "bigWin" && candidateOutcome === "bigWin") return true;
  if (lastOutcome === "loss" && candidateOutcome === "loss") return true;

  return false;
};

// ─── Public: localStorage TTL helpers ────────────────────────────────────

/**
 * Read the shown-self-tx-id map from localStorage, prune expired
 * entries (older than `ttlMs`), and return the surviving ids as a Set.
 * Also writes the pruned map back so the file doesn't grow forever.
 *
 * Returns an empty Set if localStorage is unavailable or unreadable.
 */
export const loadShownSelfTxIds = (ttlMs = DEFAULT_SHOWN_SELF_TX_TTL_MS) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return new Set();
  }
  try {
    const raw = window.localStorage.getItem(SHOWN_SELF_TX_STORAGE_KEY);
    const parsed = safeParse(raw);
    if (!parsed || typeof parsed !== "object") return new Set();

    const now = Date.now();
    const survivors = {};
    for (const [id, shownAt] of Object.entries(parsed)) {
      if (typeof shownAt !== "number") continue;
      if (now - shownAt <= ttlMs) {
        survivors[id] = shownAt;
      }
    }
    // Persist the pruned shape so stale ids drop off over time.
    window.localStorage.setItem(
      SHOWN_SELF_TX_STORAGE_KEY,
      JSON.stringify(survivors),
    );
    return new Set(Object.keys(survivors));
  } catch (err) {
    logger.warn("activityStreams: loadShownSelfTxIds failed", err?.message);
    return new Set();
  }
};

/**
 * Record that a given transaction id has now been shown to the user.
 * Stamps `Date.now()` so the TTL prune in loadShownSelfTxIds can age it
 * out later. Silently no-ops if localStorage is unavailable.
 */
export const recordShownSelfTxId = (txId) => {
  if (!txId) return;
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const raw = window.localStorage.getItem(SHOWN_SELF_TX_STORAGE_KEY);
    const parsed = safeParse(raw) || {};
    parsed[txId] = Date.now();
    window.localStorage.setItem(
      SHOWN_SELF_TX_STORAGE_KEY,
      JSON.stringify(parsed),
    );
  } catch (err) {
    logger.warn("activityStreams: recordShownSelfTxId failed", err?.message);
  }
};

// ─── Public: backbone vs fake picker ─────────────────────────────────────

/**
 * Pick the next row to render at a ticker tick.
 *
 * Decision tree:
 *   1. Roll vs `backboneProbability`. If the roll succeeds AND the
 *      backbone pool has at least one unseen, non-adjacent candidate,
 *      pick the freshest one and mark it `source: 'real'`.
 *   2. Otherwise, fall through to `fakeGenerator(realGames, currentFeed)`
 *      which already enforces no-adjacency for fake rows.
 *
 * The function is pure — it does NOT mutate `backbonePool`. Callers are
 * responsible for adding the returned row's id into their "consumed"
 * set so it isn't re-picked on the next tick.
 *
 * @param {Object} args
 * @param {Array}    args.backbonePool         FIFO of backbone activities (newest first).
 * @param {Set}      args.consumedBackboneIds  Backbone ids already shown this session.
 * @param {Object}   args.lastShown            The current head of the feed (for adjacency).
 * @param {Function} args.fakeGenerator        (realGames, currentFeed) => fakeActivity
 * @param {Array}    args.currentFeed          The feed snapshot (for fake adjacency).
 * @param {Array}    args.realGames            Real-game pool for the fake generator.
 * @param {number}   [args.backboneProbability]
 * @returns {Object} a row ready to drop into the feed
 */
export const pickNextTickerRow = ({
  backbonePool,
  consumedBackboneIds,
  lastShown,
  fakeGenerator,
  currentFeed,
  realGames,
  backboneProbability = DEFAULT_BACKBONE_PICK_PROBABILITY,
}) => {
  const fallbackToFake = () => fakeGenerator(realGames, currentFeed);

  // Guard: the backbone pool must be a real array.
  const pool = Array.isArray(backbonePool) ? backbonePool : [];
  if (pool.length === 0) return fallbackToFake();

  // Only roll the backbone path if a usable candidate exists.
  const consumed = consumedBackboneIds instanceof Set
    ? consumedBackboneIds
    : new Set();

  const candidate = pool.find(
    (row) =>
      row &&
      row.id &&
      !consumed.has(row.id) &&
      !violatesAdjacency(row, lastShown),
  );
  if (!candidate) return fallbackToFake();

  // Coin flip — most ticks should still be fake.
  if (Math.random() >= backboneProbability) return fallbackToFake();

  return {
    ...candidate,
    // Defensive: backend already sets source: 'real', but if a future
    // endpoint forgets, downstream UI still needs the marker.
    source: candidate.source || "real",
  };
};

// Exported for tests / Test Agent.
export const __testables = {
  classifyOutcome,
  violatesAdjacency,
  isBigWinActivity,
  SHOWN_SELF_TX_STORAGE_KEY,
};
